/**
 * Zenprexi Bio-Risk™ Login Page
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store';
import { login } from '@/store/authSlice';
import { UserRole } from '@/types';
import { ShieldCheckIcon, EnvelopeIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isLoading } = useAppSelector((state) => state.auth);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    
    try {
      const result = await dispatch(login({ email, password })).unwrap();
      const normalizedRole = (result.user.role || '').toString().toUpperCase();
      const fallbackName = normalizedRole === UserRole.WORKER ? 'Worker' : 'User';
      toast.success(`Welcome back, ${result.user.firstName || fallbackName}!`);
      if (normalizedRole === UserRole.WORKER) {
        navigate('/workers');
      } else {
        navigate('/');
      }
    } catch (error) {
      toast.error(error as string || 'Login failed');
    }
  };
  
  const handleDemoLogin = () => {
    setEmail('demo@zenprexi.com');
    setPassword('demo123');
  };
  
  return (
    <div className="min-h-screen bg-surface-900 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheckIcon className="h-12 w-12 text-teal-500" />
          </div>
          <h1 className="text-2xl font-bold text-white">
            ZENPREXI <span className="text-teal-400">BIO-RISK™</span>
          </h1>
          <p className="mt-2 text-sm text-gray-400">Supervisor Dashboard</p>
        </div>
      </div>
      
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="card-elevated">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input pl-10"
                  placeholder="supervisor@company.com"
                />
              </div>
            </div>
            
            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LockClosedIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pl-10"
                  placeholder="••••••••"
                />
              </div>
            </div>
            
            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>
          
          {/* Demo login */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-surface-600" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-surface-800 text-gray-400">Or</span>
              </div>
            </div>
            
            <button
              type="button"
              onClick={handleDemoLogin}
              className="mt-4 btn-secondary w-full"
            >
              Use Demo Credentials
            </button>
          </div>
        </div>
        
        {/* Footer */}
        <p className="mt-8 text-center text-xs text-gray-500">
          Patent Pending: US 63/919,896
          <br />
          © 2026 Zenprexi Inc. All rights reserved.
        </p>
      </div>
    </div>
  );
}
